// src/mocks/damContents.ts
// Содержимое предзагруженных файлов DAM (FR-1). Статика: офлайн, детерминированно.
// text — pdf/docx/pptx; table — xlsx. Используется для скачивания (fileDownload)
// и скрытого контекста агента (wrapAgentMessage).

export interface DamTextContent {
  kind: 'text';
  /** bodyMd — исходный markdown секции (есть только у файлов, созданных агентом);
   *  body — санитизированный плоский текст для контекста агента и txt-фолбэка */
  sections: Array<{ heading: string; body: string; bodyMd?: string }>;
}

export interface DamTableContent {
  kind: 'table';
  headers: string[];
  rows: string[][];
}

export type DamFileContent = DamTextContent | DamTableContent;

/** Плоский текст для контекста агента и txt-фолбэков */
export function contentToPlainText(content: DamFileContent): string {
  if (content.kind === 'text') {
    return content.sections.map((s) => `${s.heading}\n${s.body}`).join('\n\n');
  }
  return [content.headers.join(' | '), ...content.rows.map((r) => r.join(' | '))].join('\n');
}

export const DAM_CONTENTS: Record<string, DamFileContent> = {
  // ── Регламенты ЦБ ──
  'cb-1': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение',
        body:
          'Положение № 716-П устанавливает требования к системе управления операционным ' +
          'риском в кредитной организации. Документ определяет классификацию событий ' +
          'операционного риска, порядок ведения базы событий, расчёт контрольных ' +
          'показателей и требования к отчётности перед Банком России.',
      },
      {
        heading: '2. Ключевые требования к банку',
        body:
          'Банк обязан вести базу событий операционного риска с фиксацией валовых потерь, ' +
          'возмещений и причин. Назначается служба управления операционным риском, ' +
          'независимая от бизнес-подразделений. Ежеквартально рассчитываются контрольные ' +
          'показатели уровня операционного риска (КПУР) и доводятся до правления. ' +
          'Превышение сигнальных значений требует плана корректирующих мероприятий.',
      },
      {
        heading: '3. Применение в банке',
        body:
          'Ответственное подразделение — департамент рисков (Волков А. Н.). Внутренние ' +
          'процедуры приведены в соответствие с Положением: реестр событий ведётся ' +
          'в АС «Риск-Монитор», пороговое значение регистрируемой потери — 10 тыс. руб. ' +
          'Очередная самооценка запланирована на III квартал 2026 года.',
      },
    ],
  },

  'cb-2': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение',
        body:
          'Положение № 683-П определяет требования к обеспечению защиты информации ' +
          'при осуществлении банковской деятельности. Документ регулирует обязательные ' +
          'меры защиты при доступе к банковским информационным системам, требования ' +
          'к криптозащите и порядок уведомления Банка России об инцидентах.',
      },
      {
        heading: '2. Обязательные меры защиты',
        body:
          'Банк обязан применять СКЗИ, сертифицированные ФСБ России, при передаче ' +
          'персональных данных клиентов и осуществлении платёжных операций. ' +
          'Вводится требование к многофакторной аутентификации пользователей систем ' +
          'дистанционного банковского обслуживания. Внешние подключения проходят ' +
          'через демилитаризованную зону с межсетевым экраном уровня L7.',
      },
      {
        heading: '3. Статус соответствия',
        body:
          'Банк прошёл аудит соответствия 683-П в апреле 2026 года (аудитор — АО «ИнфоСек»). ' +
          'Замечаний по обязательным требованиям не выявлено. Рекомендации по усилению ' +
          'мониторинга привилегированных учётных записей реализуются в рамках дорожной ' +
          'карты ИБ до 01.09.2026. Ответственный — Волков А. Н., департамент рисков.',
      },
    ],
  },

  'cb-3': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение инструкции',
        body:
          'Инструкция № 199-И устанавливает обязательные нормативы банков: достаточность ' +
          'капитала (Н1.0, Н1.1, Н1.2), ликвидность (Н2, Н3, Н4), максимальный риск ' +
          'на одного заёмщика (Н6), крупные кредитные риски (Н7). Нарушение нормативов ' +
          'влечёт надзорные меры вплоть до отзыва лицензии.',
      },
      {
        heading: '2. Ключевые нормативы и пороги',
        body:
          'Норматив достаточности базового капитала (Н1.1) — не менее 4,5 %. ' +
          'Норматив мгновенной ликвидности (Н2) — не менее 15 %. ' +
          'Норматив текущей ликвидности (Н3) — не менее 50 %. ' +
          'Норматив долгосрочной ликвидности (Н4) — не более 120 %. ' +
          'Норматив максимального риска на заёмщика (Н6) — не более 25 % капитала.',
      },
      {
        heading: '3. Отчётность и контроль',
        body:
          'Расчёт нормативов производится ежедневно в автоматическом режиме в АБС банка. ' +
          'Сводный отчёт по обязательным нормативам передаётся в Банк России в составе ' +
          'формы 0409135. Ответственный за корректность расчётов — операционный директор ' +
          '(Волков А. Н.). По состоянию на май 2026 года все нормативы соблюдены с запасом.',
      },
    ],
  },

  'cb-4': {
    kind: 'text',
    sections: [
      {
        heading: '1. Область применения',
        body:
          'Указание № 5782-У определяет состав, сроки, порядок формирования и представления ' +
          'банками отчётности в Банк России. Указание регулирует перечень форм отчётности, ' +
          'периодичность их представления и форматы файлов передачи. ' +
          'Требования распространяются на все кредитные организации — резиденты РФ.',
      },
      {
        heading: '2. Основные формы и сроки',
        body:
          'Форма 0409101 (баланс) — ежедневно до 10:00 следующего рабочего дня. ' +
          'Форма 0409135 (нормативы ликвидности) — ежедневно. ' +
          'Форма 0409806 (отчёт о финансовых результатах) — ежемесячно до 10-го числа. ' +
          'Нарушение сроков влечёт наложение штрафа до 1 % минимального уставного капитала ' +
          'за каждый календарный день просрочки.',
      },
      {
        heading: '3. Организация в банке',
        body:
          'Подготовку регуляторной отчётности обеспечивает операционный департамент ' +
          'при методологическом контроле департамента рисков. Волков А. Н. — ответственный ' +
          'за соблюдение сроков и форматов. АБС формирует отчёты в XML-формате XBRL; ' +
          'проверка и подписание — через систему Банк России «Регулятор-Онлайн».',
      },
    ],
  },

  'cb-5': {
    kind: 'text',
    sections: [
      {
        heading: '1. Введение',
        body:
          'Методичка подготовлена для операционистов фронт-офиса в соответствии с ' +
          'требованиями Федерального закона № 115-ФЗ «О противодействии легализации ' +
          'доходов, полученных преступным путём». Документ охватывает обязанности ' +
          'сотрудника при идентификации клиентов, фиксировании подозрительных операций ' +
          'и взаимодействии с подразделением комплаенс.',
      },
      {
        heading: '2. Идентификация клиентов',
        body:
          'При открытии счёта физическому лицу операционист обязан проверить паспорт, ' +
          'сличить фото, занести данные в АБС и направить запрос в систему проверки ' +
          'санкционных списков. Для юридических лиц дополнительно запрашиваются ' +
          'документы на бенефициарного владельца. Все копии сохраняются в досье клиента ' +
          'не менее 5 лет со дня окончания отношений.',
      },
      {
        heading: '3. Действия при подозрительных операциях',
        body:
          'При выявлении признаков необычной операции сотрудник не вправе уведомлять ' +
          'клиента о проводимой проверке. Необходимо незамедлительно направить ' +
          'внутреннее сообщение в службу комплаенс через систему ПОД/ФТ-Офис. ' +
          'Комплаенс принимает решение о направлении в Росфинмониторинг в срок до 3 дней. ' +
          'Автор методички — Смирнова О. И., операционный департамент.',
      },
    ],
  },

  'cb-6': {
    kind: 'text',
    sections: [
      {
        heading: '1. Обзор изменений Q2 2026',
        body:
          'Во втором квартале 2026 года Банк России принял и опубликовал 14 нормативных ' +
          'актов, значимых для кредитных организаций. Ключевые темы: пересмотр ' +
          'требований к резервированию по ипотечным кредитам, уточнение порядка расчёта ' +
          'показателя долговой нагрузки физических лиц (ПДН), новые требования к ' +
          'раскрытию информации по ESG-факторам.',
      },
      {
        heading: '2. Существенные изменения',
        body:
          'С 01.07.2026 вступают в силу поправки к Положению 590-П в части надбавок ' +
          'к коэффициентам риска по необеспеченным потребительским кредитам с ПДН > 50 %. ' +
          'Банкам с базовой лицензией предоставлена отсрочка до 01.10.2026. ' +
          'Обновлены форматы XMLschema для форм 0409807 и 0409808. ' +
          'Введена обязательная публикация политики устойчивого развития для банков ' +
          'с активами свыше 500 млрд руб.',
      },
      {
        heading: '3. Необходимые действия банка',
        body:
          'Юридический блок (Иванова А. С.) формирует реестр нормативных актов ' +
          'с анализом применимости к текущей деятельности. Для каждого существенного ' +
          'изменения определяется дорожная карта адаптации с ответственным и сроком. ' +
          'Следующий обзор запланирован на 01.07.2026 по итогам официального вступления ' +
          'в силу новых требований.',
      },
    ],
  },

  // ── Договоры ──
  'ct-1': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет договора',
        body:
          'Банк открывает Клиенту расчётный счёт в валюте Российской Федерации и ' +
          'осуществляет расчётно-кассовое обслуживание: приём и зачисление поступающих ' +
          'средств, исполнение распоряжений Клиента о переводе, выдачу наличных. ' +
          'Перечень услуг и тарифы определяются Сборником тарифов Банка.',
      },
      {
        heading: '2. Права и обязанности сторон',
        body:
          'Банк обязуется исполнять надлежаще оформленные распоряжения Клиента не позднее ' +
          'следующего операционного дня. Банк вправе отказать в исполнении операции при ' +
          'непредставлении документов, запрошенных в рамках 115-ФЗ. Клиент обязуется ' +
          'уведомлять Банк об изменении учредительных документов в течение 5 рабочих дней.',
      },
      {
        heading: '3. Ответственность и срок действия',
        body:
          'За несвоевременное исполнение распоряжений Банк уплачивает неустойку в размере ' +
          '0,01 % от суммы за каждый день просрочки. Договор заключается на неопределённый ' +
          'срок; каждая из сторон вправе расторгнуть его с предварительным уведомлением ' +
          'за 30 календарных дней. Споры рассматриваются в Арбитражном суде г. Москвы.',
      },
    ],
  },

  'ct-2': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет кредитного договора',
        body:
          'Банк предоставляет Заёмщику — юридическому лицу — кредит в рублях Российской ' +
          'Федерации на цели пополнения оборотного капитала либо финансирования ' +
          'инвестиционной деятельности. Сумма, процентная ставка, график погашения и ' +
          'обеспечение определяются в Приложении № 1, являющемся неотъемлемой частью договора.',
      },
      {
        heading: '2. Обеспечение и ковенанты',
        body:
          'В качестве обеспечения Заёмщик предоставляет залог имущества и/или ' +
          'поручительство связанных лиц согласно перечню в Приложении № 2. ' +
          'Заёмщик обязуется поддерживать ежеквартальный оборот по расчётным счетам ' +
          'не ниже суммы кредита, раскрывать финансовую отчётность ежеквартально и ' +
          'не допускать снижения чистых активов ниже уставного капитала.',
      },
      {
        heading: '3. Досрочное погашение и штрафы',
        body:
          'Заёмщик вправе досрочно погасить кредит полностью или частично с уведомлением ' +
          'Банка за 5 рабочих дней. При просрочке начисляется штрафная ставка в размере ' +
          'двукратной ключевой ставки ЦБ к сумме просроченного долга. ' +
          'Споры рассматриваются в Арбитражном суде по месту нахождения Банка.',
      },
    ],
  },

  'ct-3': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет договора',
        body:
          'Настоящий договор заключён между банком и ООО «Диасофт» на поставку, ' +
          'внедрение и техническую поддержку банковского программного обеспечения ' +
          'в составе АБС «Диасофт FLEXTERA». Стоимость договора составляет 48 750 000 руб., ' +
          'НДС не облагается на основании п. 26 ст. 149 НК РФ.',
      },
      {
        heading: '2. Сроки и этапы поставки',
        body:
          'Этап 1 (базовая поставка ПО) — в течение 30 дней с даты подписания. ' +
          'Этап 2 (пуско-наладочные работы) — 90 рабочих дней. ' +
          'Этап 3 (опытная эксплуатация) — 60 рабочих дней. ' +
          'Поставщик гарантирует работоспособность систем в соответствии с SLA: ' +
          'доступность ядра АБС — не менее 99,5 % в рабочее время.',
      },
      {
        heading: '3. Ответственность и конфиденциальность',
        body:
          'Стороны обязуются не разглашать технические спецификации, условия сделки ' +
          'и персональные данные, полученные в ходе исполнения договора. ' +
          'За нарушение SLA Поставщик выплачивает штраф до 0,5 % стоимости договора ' +
          'за каждые сутки сверхнормативного простоя. Ответственный от банка — Петров М. В.',
      },
    ],
  },

  'ct-4': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет договора аренды',
        body:
          'Арендодатель — ООО «Лесная Девелопмент» — передаёт Банку в аренду нежилые ' +
          'помещения общей площадью 2 340 кв. м на 4-м и 5-м этажах бизнес-центра «Лесная 7» ' +
          'по адресу: г. Москва, ул. Лесная, д. 7. Назначение — размещение головного офиса банка.',
      },
      {
        heading: '2. Арендная плата и эксплуатационные расходы',
        body:
          'Ежемесячная арендная плата составляет 3 510 000 руб., включая НДС 20 %. ' +
          'Эксплуатационные расходы (коммунальные, уборка, охрана) — отдельно, ' +
          'по фактическим затратам Арендодателя. Индексация арендной платы возможна ' +
          'раз в год не более чем на официальный индекс потребительских цен. ' +
          'Оплата — не позднее 5-го числа текущего месяца.',
      },
      {
        heading: '3. Срок аренды и условия расторжения',
        body:
          'Договор заключён сроком на 5 лет с даты подписания акта приёма-передачи. ' +
          'Каждая сторона вправе расторгнуть договор досрочно с уведомлением за 6 месяцев. ' +
          'При досрочном расторжении по инициативе Арендодателя без вины Арендатора ' +
          'выплачивается компенсация в размере 3 ежемесячных арендных плат.',
      },
    ],
  },

  'ct-5': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет договора эквайринга',
        body:
          'Банк (Эквайер) обеспечивает приём платёжных карт Visa, Mastercard и «Мир» ' +
          'в торгово-сервисных точках Клиента на территории Российской Федерации. ' +
          'Банк устанавливает и обслуживает POS-терминалы, обрабатывает транзакции, ' +
          'зачисляет средства на расчётный счёт Клиента за вычетом комиссии эквайра.',
      },
      {
        heading: '2. Тарифы и взаиморасчёты',
        body:
          'Комиссия за эквайринг: 1,4 % от суммы транзакции для карт «Мир», ' +
          '1,6 % для карт Visa/Mastercard. Зачисление средств — на следующий рабочий ' +
          'день после авторизации. Спорные транзакции рассматриваются в течение 45 дней. ' +
          'Банк обеспечивает поддержку 3DS2-аутентификации для интернет-транзакций.',
      },
      {
        heading: '3. Ответственность и расторжение',
        body:
          'Клиент несёт ответственность за сохранность терминального оборудования; ' +
          'повреждение оборудования по вине Клиента возмещается по прайс-листу Банка. ' +
          'Договор действует 2 года с автоматической пролонгацией. ' +
          'Расторжение — по письменному уведомлению за 30 дней. ' +
          'Редакция 3 согласована юридическим отделом 07.05.2026 (Иванова А. С.).',
      },
    ],
  },

  'ct-6': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет сделки',
        body:
          'Банк (Продавец) реализует простые процентные векселя в количестве 5 штук ' +
          'номиналом 10 000 000 руб. каждый (всего 50 000 000 руб.) Покупателю — ' +
          'АО «ИнвестКапитал». Процентная ставка — 16,5 % годовых. ' +
          'Срок предъявления к платежу — по предъявлении, но не ранее 180 дней с даты выдачи.',
      },
      {
        heading: '2. Порядок расчётов',
        body:
          'Оплата производится безналичным переводом на корреспондентский счёт Банка ' +
          'в день подписания договора. Векселя передаются Покупателю по акту ' +
          'приёма-передачи ценных бумаг одновременно с поступлением средств. ' +
          'Налогообложение дохода по векселям — в соответствии с НК РФ, ' +
          'Банк выступает налоговым агентом.',
      },
      {
        heading: '3. Реквизиты и ответственность',
        body:
          'Ответственный за ведение сделки от Банка — Петров М. В., юридический блок. ' +
          'В случае утраты векселя Покупатель обязан незамедлительно уведомить Банк ' +
          'и обратиться в суд за признанием ценной бумаги недействительной. ' +
          'Договор зарегистрирован под № 14-2026 от 25.04.2026.',
      },
    ],
  },

  'ct-7': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет агентского договора',
        body:
          'Банк (Принципал) поручает ООО «СтрахКонсалт» (Агент) осуществлять ' +
          'страховое агентирование: привлекать клиентов банка к заключению договоров ' +
          'страхования жизни и имущества в аккредитованных страховых компаниях. ' +
          'Агент действует от имени и за счёт Принципала в рамках выданной доверенности.',
      },
      {
        heading: '2. Вознаграждение и отчётность',
        body:
          'Агентское вознаграждение составляет 15 % страховой премии по каждому ' +
          'оформленному полису. Выплата — ежемесячно, на основании отчёта Агента ' +
          'с приложением копий договоров страхования. Общий объём вознаграждения ' +
          'не ограничен; Агент обязан обеспечить не менее 120 полисов в квартал.',
      },
      {
        heading: '3. Ответственность и срок',
        body:
          'Агент несёт ответственность за достоверность информации, сообщаемой клиентам, ' +
          'и за соответствие оформленных полисов требованиям страховщика. ' +
          'При нарушении квалификационных требований к агентской деятельности ' +
          'Принципал вправе расторгнуть договор досрочно без компенсации. ' +
          'Срок договора — 1 год с автопролонгацией; исполнитель — Иванова А. С.',
      },
    ],
  },

  'ct-8': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет соглашения о конфиденциальности',
        body:
          'Банк и ООО «Синимекс» (подрядчик по IT-интеграции) заключают настоящее ' +
          'соглашение о неразглашении (NDA) в связи с предстоящими переговорами об ' +
          'интеграции процессинговой системы Синимекс с АБС Банка. ' +
          'Конфиденциальной считается любая техническая, финансовая и деловая информация, ' +
          'переданная сторонами в ходе сотрудничества.',
      },
      {
        heading: '2. Обязательства сторон',
        body:
          'Стороны обязуются не раскрывать конфиденциальную информацию третьим лицам ' +
          'без письменного согласия другой стороны, использовать её исключительно ' +
          'в целях оценки возможности сотрудничества, обеспечить режим ограниченного ' +
          'доступа внутри своих организаций. Срок конфиденциальности — 3 года.',
      },
      {
        heading: '3. Ответственность и разрешение споров',
        body:
          'За нарушение NDA виновная сторона выплачивает штраф 5 000 000 руб. ' +
          'и возмещает доказанные убытки сверх штрафа. ' +
          'Споры разрешаются в Арбитражном суде г. Москвы. ' +
          'Соглашение подписано 02.06.2026; ответственный от Банка — Петров М. В.',
      },
    ],
  },

  // ── Юридический отдел ──
  'lg-1': {
    kind: 'text',
    sections: [
      {
        heading: '1. Предмет заключения',
        body:
          'Настоящее заключение подготовлено юридическим блоком по запросу правления ' +
          'относительно правовой природы цифрового рубля, введённого Федеральным законом ' +
          '№ 259-ФЗ. Документ анализирует статус цифрового рубля как законного средства ' +
          'платежа, условия открытия цифровых кошельков и риски для банков-участников платформы.',
      },
      {
        heading: '2. Основные правовые выводы',
        body:
          'Цифровой рубль является третьей формой национальной валюты наряду с наличными ' +
          'и безналичными средствами. Банки обязаны обеспечить прём цифрового рубля ' +
          'в торговых точках клиентов с 01.07.2025. Проценты на остаток цифровых рублей ' +
          'не начисляются; кредитование в цифровых рублях законодательством не предусмотрено. ' +
          'ЦБ обязан принять меры по ошибочным операциям в течение 5 дней.',
      },
      {
        heading: '3. Рекомендации банку',
        body:
          'Обновить типовые договоры РКО с включением раздела о цифровом рубле. ' +
          'Разработать регламент открытия цифровых кошельков и тарифы на операции. ' +
          'Подключиться к платформе цифрового рубля ЦБ до 01.01.2026. ' +
          'Юридический блок рекомендует провести обучение операционистов в июне 2026. ' +
          'Ответственный — Иванова А. С.',
      },
    ],
  },

  'lg-2': {
    kind: 'text',
    sections: [
      {
        heading: '1. Описание спора',
        body:
          'Настоящая претензия направлена в адрес АО «Альфа-Лизинг» в связи с ' +
          'нарушением условий договора финансовой аренды № 12-2025 от 14.03.2025. ' +
          'АО «Альфа-Лизинг» не исполнило обязательство по передаче предмета лизинга ' +
          '(производственного оборудования) в срок, предусмотренный договором, ' +
          'чем причинило Банку убытки в размере 3 200 000 руб.',
      },
      {
        heading: '2. Требования',
        body:
          'Банк требует от АО «Альфа-Лизинг»: 1) передать предмет лизинга в течение ' +
          '10 рабочих дней с даты получения настоящей претензии; 2) уплатить неустойку ' +
          'за просрочку в размере 0,1 % от стоимости предмета лизинга за каждый день — ' +
          'итого 1 440 000 руб. за 45 дней; 3) возместить иные убытки по выставленным счетам.',
      },
      {
        heading: '3. Порядок урегулирования',
        body:
          'Ответ на претензию ожидается в течение 15 рабочих дней. При отсутствии ' +
          'ответа или неудовлетворительном урегулировании Банк обратится в ' +
          'Арбитражный суд г. Москвы. Претензия составлена юридическим блоком ' +
          '(Петров М. В.), исх. № 245 от 26.05.2026.',
      },
    ],
  },

  'lg-3': {
    kind: 'text',
    sections: [
      {
        heading: '1. Введение',
        body:
          'Настоящий обзор подготовлен юридическим блоком и охватывает судебную практику ' +
          'по Федеральному закону № 353-ФЗ «О потребительском кредите (займе)» за период ' +
          '2024–2026 годов. Анализ базируется на решениях арбитражных судов, Верховного ' +
          'суда РФ и Банка России по делам о нарушении прав заёмщиков.',
      },
      {
        heading: '2. Ключевые позиции судов',
        body:
          'Суды устойчиво признают незаконным взимание комиссий, не включённых в ПСК ' +
          '(полную стоимость кредита). Страховой продукт, навязанный без права отказа, ' +
          'расценивается как нарушение ст. 7 353-ФЗ. Срок исковой давности по ' +
          'оспариванию ПСК — 3 года с момента заключения договора. ' +
          'Судебный порядок — единственный способ оспорить ПСК после подписания.',
      },
      {
        heading: '3. Риски для банка и рекомендации',
        body:
          'Риск: взыскание переплаченных комиссий в ретроспективе. ' +
          'Рекомендуется провести аудит кредитных продуктов на соответствие ПСК. ' +
          'Стандартные договоры скорректированы согласно актуальной практике. ' +
          'Обзор подготовлен Ивановой А. С.; следующий выпуск — Q3 2026.',
      },
    ],
  },

  'lg-4': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение шаблона',
        body:
          'Настоящий шаблон разработан юридическим блоком для оформления нотариальной ' +
          'доверенности от имени генерального директора банка на совершение ' +
          'процессуальных и корпоративных действий. Шаблон учитывает требования ' +
          'гл. 10 ГК РФ и актуальную нотариальную практику.',
      },
      {
        heading: '2. Типовые полномочия',
        body:
          'Доверенность предоставляет поверенному право: представлять банк в судах ' +
          'всех инстанций, подписывать процессуальные документы, заключать мировые ' +
          'соглашения с одобрения правления, получать исполнительные листы, ' +
          'подавать заявления в регистрирующие органы, открывать и закрывать банковские счета. ' +
          'Передоверие — запрещено без отдельного указания.',
      },
      {
        heading: '3. Инструкция по применению',
        body:
          'Перед оформлением уточнить перечень полномочий у инициирующего подразделения. ' +
          'Заверение — у нотариуса, с предъявлением устава и решения о назначении директора. ' +
          'Срок действия указывать явно (максимум — 3 года). ' +
          'Копию нотариально заверенной доверенности передать в архив юридического блока. ' +
          'Ответственный — Петров М. В.',
      },
    ],
  },

  'lg-5': {
    kind: 'text',
    sections: [
      {
        heading: '1. Цель и область применения',
        body:
          'Регламент устанавливает единый порядок согласования, заключения, учёта ' +
          'и хранения договоров банка с контрагентами. Требования распространяются ' +
          'на все структурные подразделения; исключение — типовые договоры РКО и ' +
          'кредитные договоры, регулируемые отдельными регламентами.',
      },
      {
        heading: '2. Порядок согласования',
        body:
          'Инициирующее подразделение формирует проект договора и направляет его ' +
          'на согласование в юридический блок (срок — 5 рабочих дней), ' +
          'департамент рисков (3 дня для сделок > 50 млн руб.) и финансовый контроль. ' +
          'Договоры свыше 100 млн руб. требуют одобрения кредитного комитета. ' +
          'Согласование ведётся в системе электронного документооборота (СЭД).',
      },
      {
        heading: '3. Хранение и контроль исполнения',
        body:
          'Оригиналы договоров хранятся в архиве юридического блока; сроки хранения — ' +
          '5 лет после истечения срока действия. ' +
          'Юридический блок ведёт реестр договоров в СЭД: даты, суммы, статусы, ' +
          'контрольные точки исполнения. Регламент версии 4 утверждён 08.05.2026; ' +
          'ответственный — Иванова А. С.',
      },
    ],
  },

  'lg-6': {
    kind: 'text',
    sections: [
      {
        heading: '1. Состав участников',
        body:
          'Внеочередное заседание правления банка № 18 состоялось 27.05.2026 в 14:00. ' +
          'Присутствовали: председатель правления, три члена правления, ' +
          'директор по рискам, главный юрист. Кворум: 5 из 5 членов правления — имеется.',
      },
      {
        heading: '2. Повестка и принятые решения',
        body:
          'Вопрос 1: Утверждение кредитной политики на 2026 год — принято единогласно. ' +
          'Вопрос 2: Одобрение сделки по кредитованию АО «АгроКомплект» на 95,1 млн руб. — ' +
          'принято 4 голосами за, 1 воздержался (директор по рискам). ' +
          'Вопрос 3: Принятие к сведению отчёта об аудите по 683-П — принято единогласно.',
      },
      {
        heading: '3. Поручения',
        body:
          'Юридическому блоку: оформить сделку с АО «АгроКомплект» до 05.06.2026. ' +
          'Департаменту рисков: разработать план устранения замечаний из аудита до 01.09.2026. ' +
          'HR: обеспечить прохождение обязательного обучения по 115-ФЗ для новых сотрудников ' +
          'фронт-офиса до 30.06.2026. Протокол подписан 27.05.2026; секретарь — Петров М. В.',
      },
    ],
  },

  // ── HR-документы ──
  'hr-1': {
    kind: 'text',
    sections: [
      {
        heading: '1. Общие положения',
        body:
          'Политика определяет порядок планирования и предоставления ежегодных ' +
          'оплачиваемых отпусков работникам банка в 2026 году. Продолжительность ' +
          'основного отпуска — 28 календарных дней; за ненормированный рабочий день ' +
          'предоставляется дополнительный отпуск 3 дня.',
      },
      {
        heading: '2. Планирование',
        body:
          'График отпусков формируется до 15 декабря предшествующего года в HR-системе. ' +
          'Перенос отпуска согласуется с руководителем подразделения не позднее чем ' +
          'за 14 дней. Не допускается накопление более 14 неиспользованных дней ' +
          'на конец года; HR уведомляет руководителей о превышениях ежеквартально.',
      },
      {
        heading: '3. Особые случаи',
        body:
          'Работникам операционного блока отпуска в период закрытия отчётности ' +
          '(первая декада января, апреля, июля, октября) предоставляются только ' +
          'по согласованию с операционным директором. Отзыв из отпуска возможен ' +
          'с письменного согласия работника с компенсацией неиспользованных дней.',
      },
    ],
  },

  'hr-2': {
    kind: 'text',
    sections: [
      {
        heading: '1. Общие положения',
        body:
          'Положение устанавливает принципы формирования и выплаты заработной платы ' +
          'сотрудникам банка. Система оплаты труда состоит из постоянной части ' +
          '(оклад согласно штатному расписанию) и переменной части ' +
          '(квартальные и годовые премии по результатам KPI).',
      },
      {
        heading: '2. Постоянная часть и грейды',
        body:
          'Штатное расписание содержит 8 грейдов от A1 (ассистент) до A8 (директор). ' +
          'Оклады пересматриваются ежегодно на основании рыночного исследования ' +
          'заработных плат; коэффициент индексации утверждается правлением. ' +
          'Запрещено устанавливать оклад ниже прожиточного минимума в регионе присутствия.',
      },
      {
        heading: '3. Переменная часть и KPI',
        body:
          'Ключевые показатели для ежеквартальных премий: выполнение плана продаж (вес 40 %), ' +
          'качество клиентского обслуживания (30 %), операционная эффективность (30 %). ' +
          'Годовая премия рассчитывается по итогам года; максимальный размер — 4 оклада. ' +
          'Сотрудники на испытательном сроке не участвуют в программе KPI-премирования.',
      },
    ],
  },

  'hr-3': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение шаблона',
        body:
          'Шаблон разработан HR-блоком для формирования офферов кандидатам ' +
          'на должность старшего юриста (грейд A5). ' +
          'Документ содержит условия трудоустройства, предлагаемые банком, ' +
          'и является официальным предложением о найме при подписании уполномоченным лицом.',
      },
      {
        heading: '2. Условия предложения',
        body:
          'Должность: Старший юрист, юридический блок. ' +
          'Оклад: согласно уровню грейда A5, от 180 000 до 210 000 руб./мес. (до вычета НДФЛ). ' +
          'Годовой бонус: до 2 окладов по результатам KPI. ' +
          'Льготный пакет: ДМС (семья), полис ОМС, мобильная компенсация 1 000 руб./мес., ' +
          'корпоративное обучение по подписке на правовые базы данных.',
      },
      {
        heading: '3. Срок и порядок принятия',
        body:
          'Оффер действителен 5 рабочих дней с даты выдачи. ' +
          'Дата выхода — по согласованию, не позднее чем через 30 дней после принятия. ' +
          'Для принятия оффера кандидат подписывает документ и передаёт в HR. ' +
          'Принятие оффера не является заключением трудового договора; ' +
          'трудовой договор подписывается в первый рабочий день.',
      },
    ],
  },

  'hr-4': {
    kind: 'table',
    headers: [
      'Позиция',
      'Подразделение',
      'Количество вакансий',
      'Грейд',
      'Плановая дата найма',
      'Статус',
      'Ответственный HR',
    ],
    rows: [
      [
        'Старший юрист',
        'Юридический блок',
        '1',
        'A5',
        '01.07.2026',
        'Собеседования',
        'Смирнова О.',
      ],
      [
        'Операционист',
        'Операционный отдел',
        '3',
        'A2',
        '15.07.2026',
        'Скрининг резюме',
        'Смирнова О.',
      ],
      [
        'Аналитик рисков',
        'Департамент рисков',
        '2',
        'A4',
        '01.08.2026',
        'Согласование требований',
        'Смирнова О.',
      ],
      [
        'Разработчик ПО (Java)',
        'IT-департамент',
        '2',
        'A4',
        '01.08.2026',
        'Собеседования',
        'Смирнова О.',
      ],
      [
        'Менеджер по продуктам МСБ',
        'Кредитный отдел',
        '1',
        'A5',
        '01.09.2026',
        'Открыта',
        'Смирнова О.',
      ],
      [
        'Специалист комплаенс',
        'Комплаенс',
        '1',
        'A3',
        '15.07.2026',
        'Скрининг резюме',
        'Смирнова О.',
      ],
      [
        'Руководитель проекта (цифровой рубль)',
        'IT-департамент',
        '1',
        'A6',
        '01.07.2026',
        'Финальный этап',
        'Смирнова О.',
      ],
    ],
  },

  'hr-5': {
    kind: 'text',
    sections: [
      {
        heading: '1. Цели и область применения',
        body:
          'Регламент определяет порядок введения в должность новых сотрудников банка. ' +
          'Цели: снизить время до полной операционной эффективности нового сотрудника, ' +
          'сформировать правильные ожидания относительно корпоративной культуры. ' +
          'Регламент действует для всех постоянных сотрудников со дня приёма на работу.',
      },
      {
        heading: '2. Этапы адаптации',
        body:
          'День 1: оформление документов, выдача оборудования, вводный инструктаж ИБ. ' +
          'Неделя 1: знакомство с командой, обзор ключевых систем, встреча с HR-партнёром. ' +
          'Месяц 1: прохождение обязательных онлайн-курсов (115-ФЗ, ИБ, этика). ' +
          'Месяц 3: первая встреча 1-on-1 с руководителем по итогам испытательного срока.',
      },
      {
        heading: '3. Ответственность и контроль',
        body:
          'HR-партнёр составляет и сопровождает план адаптации для каждого нового сотрудника. ' +
          'Руководитель подразделения назначает наставника из числа опытных коллег. ' +
          'Контроль прохождения обязательных курсов — через LMS «Корпоративный университет». ' +
          'Регламент актуализирован 05.05.2026; ответственный — Смирнова О. И.',
      },
    ],
  },

  'hr-6': {
    kind: 'text',
    sections: [
      {
        heading: 'Слайд 1–2. Методология и охват',
        body:
          'Опрос вовлечённости проведён в мае 2026 года среди 412 сотрудников банка. ' +
          'Анонимная анкета из 32 вопросов охватывала 5 ключевых измерений: ' +
          'вовлечённость, удовлетворённость, приверженность, развитие и отношения с руководителем. ' +
          'Уровень участия — 87 %, что превышает среднеотраслевой показатель на 12 п.п.',
      },
      {
        heading: 'Слайд 3–5. Сводные результаты',
        body:
          'Индекс вовлечённости (eNPS) составил +42 — рост на 8 п.п. по сравнению с маем 2025. ' +
          'Высокий балл (4+ из 5): «Гордость за работу в банке» (4,3), «Доверие к руководству» (4,1). ' +
          'Низкий балл: «Возможности карьерного роста» (3,2), «Прозрачность информирования» (3,4). ' +
          'Операционный блок показал наибольший рост; юридический — стабильно высокий результат.',
      },
      {
        heading: 'Слайд 6–8. Запланированные действия',
        body:
          'По итогам опроса HR разрабатывает: программу развития карьерных треков (Q3 2026), ' +
          'квартальный дайджест от правления для всех сотрудников, ' +
          'серию «Открытый диалог» с топ-менеджерами раз в полгода. ' +
          'Следующий замер — ноябрь 2026. Ответственный — Смирнова О. И., HR-блок.',
      },
    ],
  },

  'hr-7': {
    kind: 'text',
    sections: [
      {
        heading: '1. Общие положения',
        body:
          'Настоящая должностная инструкция устанавливает требования к квалификации, ' +
          'должностные обязанности, права и ответственность операциониста ' +
          'расчётно-кассового обслуживания. Инструкция разработана в соответствии ' +
          'с профессиональным стандартом «Специалист по банковскому обслуживанию».',
      },
      {
        heading: '2. Должностные обязанности',
        body:
          'Операционист обязан: открывать и вести счета физических и юридических лиц; ' +
          'принимать и обрабатывать платёжные поручения клиентов; ' +
          'идентифицировать клиентов в соответствии с 115-ФЗ; ' +
          'выдавать и принимать наличные денежные средства; ' +
          'консультировать клиентов о банковских продуктах и тарифах. ' +
          'Запрещено покидать рабочее место с незакрытой кассой.',
      },
      {
        heading: '3. Требования к квалификации',
        body:
          'Образование: высшее профессиональное (финансы, экономика, банковское дело). ' +
          'Опыт работы: от 1 года в банковской сфере (для старшего операциониста — от 3 лет). ' +
          'Обязательные сертификаты: курс по ПОД/ФТ (115-ФЗ) не реже 1 раза в 3 года. ' +
          'Инструкция утверждена 20.04.2026; ответственный за актуализацию — Смирнова О. И.',
      },
    ],
  },

  // ── Кредитование ──
  'cr-1': {
    kind: 'text',
    sections: [
      {
        heading: '1. Стратегические цели',
        body:
          'Кредитная политика на 2026 год определяет приоритеты развития кредитного ' +
          'портфеля банка: рост портфеля МСБ не менее 15 % к концу года, ' +
          'снижение доли просроченной задолженности ниже 3,5 % NPL, ' +
          'сохранение доли I–II категорий качества не менее 92 %.',
      },
      {
        heading: '2. Приоритетные сегменты и продукты',
        body:
          'Приоритет 1 — кредиты МСБ на оборотный капитал сроком до 3 лет, ' +
          'ставка 15–18 % при наличии оборота по расчётному счёту. ' +
          'Приоритет 2 — инвестиционные кредиты под залог недвижимости сроком до 7 лет. ' +
          'Ограничение: кредиты физическим лицам без обеспечения — не более 5 % портфеля. ' +
          'Новые продукты рассматриваются только по согласованию с правлением.',
      },
      {
        heading: '3. Лимиты и риск-аппетит',
        body:
          'Лимит концентрации на одного заёмщика — не более 20 % капитала (< норматива Н6). ' +
          'Отраслевые лимиты: строительство — max 20 % портфеля, ' +
          'торговля — max 30 %, сельское хозяйство — max 10 %. ' +
          'Кредиты свыше 100 млн руб. требуют одобрения кредитного комитета. ' +
          'Документ утверждён правлением 15.04.2026; ответственный — Волков А. Н.',
      },
    ],
  },

  'cr-2': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение и область применения',
        body:
          'Регламент устанавливает порядок андеррайтинга кредитных заявок юридических лиц ' +
          'и индивидуальных предпринимателей. Требования распространяются на все ' +
          'кредитные продукты банка для ЮЛ: овердрафт, кредитная линия, срочный кредит. ' +
          'Заявки физических лиц регулируются отдельным регламентом.',
      },
      {
        heading: '2. Этапы андеррайтинга',
        body:
          'Этап 1 — формальная проверка: полнота пакета документов, проверка по стоп-листам, ' +
          'ПОД/ФТ-проверка. Срок — 1 рабочий день. ' +
          'Этап 2 — финансовый анализ: расчёт финансовых коэффициентов, оценка денежного потока, ' +
          'стресс-тест доходов. Срок — 3 рабочих дня. ' +
          'Этап 3 — кредитное решение: скоринговая модель МСБ v2.4, решение комитета. ' +
          'Срок — 2 рабочих дня.',
      },
      {
        heading: '3. Критерии одобрения',
        body:
          'Минимальный оборот по счёту: не менее 150 % суммы запрашиваемого кредита в год. ' +
          'Минимальный срок деятельности ЮЛ: 18 месяцев. ' +
          'Отношение долг/EBITDA: не более 4x. ' +
          'Покрытие обеспечения: не менее 120 % суммы кредита с учётом залогового дисконта. ' +
          'Регламент утверждён 11.05.2026; ответственный — Волков А. Н.',
      },
    ],
  },

  'cr-3': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение модели',
        body:
          'Скоринговая модель МСБ v2.4 предназначена для автоматизированной оценки ' +
          'кредитоспособности субъектов малого и среднего бизнеса при рассмотрении ' +
          'заявок на кредитование. Модель разработана банком совместно с ООО «СкорТех» ' +
          'и обновлена в мае 2024 года по данным исторического портфеля 2019–2023 годов.',
      },
      {
        heading: '2. Факторы и весовые коэффициенты',
        body:
          'Финансовые факторы (60 %): оборот по счёту, рентабельность, коэффициент покрытия ' +
          'долга (DSCR), динамика выручки за 2 года. ' +
          'Поведенческие факторы (25 %): история отношений с банком, просрочки по предыдущим ' +
          'кредитам, остатки на счетах. ' +
          'Качественные факторы (15 %): отрасль, срок работы, наличие обеспечения.',
      },
      {
        heading: '3. Диапазоны скоринга и решения',
        body:
          'Score 700–850: автоматическое одобрение до 15 млн руб. без визита кредитного комитета. ' +
          'Score 550–699: стандартное рассмотрение с запросом дополнительных документов. ' +
          'Score 400–549: усиленный андеррайтинг, обязательная встреча с заёмщиком. ' +
          'Score < 400: отказ без возможности апелляции на 6 месяцев. ' +
          'Ответственный — Волков А. Н., версия 2.4 введена в эксплуатацию 24.05.2026.',
      },
    ],
  },

  // ── Кредитование (xlsx) ──
  'cr-4': {
    kind: 'table',
    headers: [
      'Заёмщик',
      'Сегмент',
      'Остаток долга, млн ₽',
      'Ставка, %',
      'Категория качества',
      'Просрочка, дней',
    ],
    rows: [
      ['ООО «Вектор-Строй»', 'МСБ', '42,5', '17,5', 'II', '0'],
      ['АО «ТехноЛизинг»', 'МСБ', '78,0', '16,2', 'I', '0'],
      ['ИП Кузнецов А. В.', 'Микро', '6,8', '19,0', 'II', '4'],
      ['ООО «Прод-Опт»', 'МСБ', '31,2', '18,1', 'III', '37'],
      ['ООО «МедСервис+»', 'МСБ', '54,9', '16,8', 'I', '0'],
      ['ООО «Логистика-Юг»', 'МСБ', '23,4', '18,4', 'II', '11'],
      ['АО «АгроКомплект»', 'МСБ', '95,1', '15,9', 'I', '0'],
      ['ИП Соколова М. И.', 'Микро', '3,2', '19,5', 'II', '0'],
    ],
  },

  'cr-5': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение анкеты',
        body:
          'Анкета заёмщика — юридического лица предназначена для сбора информации, ' +
          'необходимой для первичной оценки кредитоспособности при рассмотрении заявки. ' +
          'Заполняется уполномоченным представителем организации с приложением ' +
          'правоустанавливающих документов и финансовой отчётности.',
      },
      {
        heading: '2. Разделы анкеты',
        body:
          'Раздел 1: Общие сведения (полное наименование, ОГРН, ИНН, адрес, дата регистрации). ' +
          'Раздел 2: Структура собственности и бенефициарные владельцы (доля > 25 %). ' +
          'Раздел 3: Финансовые показатели за последние 2 года (выручка, прибыль, активы). ' +
          'Раздел 4: Сведения о запрашиваемом кредите (цель, сумма, срок, обеспечение). ' +
          'Раздел 5: Кредитная история с иными банками и задолженности.',
      },
      {
        heading: '3. Порядок заполнения',
        body:
          'Анкета заполняется в электронном виде в АБС банка или в бумажном виде ' +
          'с последующей загрузкой скан-копии в систему ЭДО. ' +
          'Ответственный за первичный приём — операционист кредитного отдела. ' +
          'Шаблон актуализирован 19.04.2026; ответственный — Смирнова О. И.',
      },
    ],
  },

  'cr-6': {
    kind: 'text',
    sections: [
      {
        heading: '1. Цели и область применения',
        body:
          'Методика устанавливает порядок оценки залогового имущества, принимаемого ' +
          'в обеспечение кредитных обязательств юридических и физических лиц. ' +
          'Применяется для объектов недвижимости, транспортных средств, оборудования ' +
          'и ценных бумаг. Оценка производится при выдаче кредита, ' +
          'а также при ежегодном мониторинге залогового портфеля.',
      },
      {
        heading: '2. Методы оценки и дисконты',
        body:
          'Недвижимость: метод сравнимых продаж, рыночная стоимость × дисконт 30 %. ' +
          'Транспортные средства: экспертная оценка аккредитованного оценщика × дисконт 40 %. ' +
          'Производственное оборудование: дисконт 50 % от балансовой стоимости. ' +
          'Ценные бумаги котируемые: 90 % биржевой цены. ' +
          'Оценка производится не позднее 10 рабочих дней до выдачи кредита.',
      },
      {
        heading: '3. Мониторинг залогового портфеля',
        body:
          'Ежегодный мониторинг проводится по всем залогам с суммой более 5 млн руб. ' +
          'При снижении стоимости залога ниже 110 % суммы долга — требуется ' +
          'уведомление заёмщика и представление дополнительного обеспечения в течение 30 дней. ' +
          'Ответственный — Волков А. Н.; методика актуализирована 16.05.2026.',
      },
    ],
  },

  'cr-7': {
    kind: 'table',
    headers: [
      'Заёмщик',
      'Сегмент',
      'Сумма кредита, млн ₽',
      'Просрочка, дней',
      'Категория качества',
      'РВПС, млн ₽',
      'Статус взыскания',
    ],
    rows: [
      ['ООО «Прод-Опт»', 'МСБ', '31,2', '37', 'III', '1,87', 'Досудебная претензия'],
      ['ИП Петренко В. Г.', 'Микро', '2,8', '92', 'IV', '1,40', 'Исковое заявление'],
      ['ООО «СтройГрупп»', 'МСБ', '18,5', '62', 'III', '2,78', 'Переговоры о реструктуризации'],
      ['ООО «АвтоТрейд»', 'МСБ', '11,0', '180', 'V', '11,0', 'Принудительное взыскание'],
      ['ИП Рогова С. В.', 'Микро', '1,9', '45', 'III', '0,38', 'Досудебная претензия'],
      ['ООО «ТоргПарк»', 'МСБ', '24,7', '120', 'IV', '12,35', 'Реструктуризация оформлена'],
    ],
  },

  // ── Комплаенс и ПОД/ФТ ──
  'cp-1': {
    kind: 'text',
    sections: [
      {
        heading: '1. Правовая основа',
        body:
          'Правила внутреннего контроля (ПВК) разработаны в соответствии с Федеральным ' +
          'законом № 115-ФЗ и Положением Банка России № 375-П. Цель ПВК — предотвращение ' +
          'использования банка в целях легализации доходов, полученных преступным путём, ' +
          'и финансирования терроризма (ПОД/ФТ/ФРОМУ).',
      },
      {
        heading: '2. Программы внутреннего контроля',
        body:
          'ПВК включает 8 программ: идентификации клиентов, выявления операций, ' +
          'фиксирования и хранения сведений, подготовки и обучения сотрудников, ' +
          'проверки контрагентов (KYC), мониторинга операций, управления риском ОД/ФТ, ' +
          'взаимодействия с Росфинмониторингом. Каждая программа содержит процедуры, ' +
          'ответственных и сроки исполнения.',
      },
      {
        heading: '3. Статус и актуализация',
        body:
          'ПВК актуализированы в мае 2026 года в связи с изменениями в Федеральном ' +
          'законе 115-ФЗ (расширение перечня операций, подлежащих обязательному контролю). ' +
          'Изменения вступают в силу 01.07.2026. Ответственный за соблюдение ПВК — ' +
          'специальное должностное лицо по ПОД/ФТ (Иванова А. С.).',
      },
    ],
  },

  'cp-2': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение документа',
        body:
          'Перечень содержит критерии и признаки, по которым сотрудники банка обязаны ' +
          'идентифицировать потенциально сомнительные операции клиентов. ' +
          'Документ разработан в соответствии с рекомендациями Росфинмониторинга ' +
          'и адаптирован с учётом портфеля клиентов банка.',
      },
      {
        heading: '2. Основные признаки сомнительных операций',
        body:
          'Группа А (структурирование): дробление суммы > 600 тыс. руб. на несколько ' +
          'переводов в течение 1–3 дней. ' +
          'Группа Б (транзитные схемы): зачисление и немедленное снятие свыше 80 % суммы. ' +
          'Группа В (несоответствие профилю): операции, не характерные для вида деятельности. ' +
          'Группа Г (санкционные риски): контрагенты из юрисдикций с повышенным риском.',
      },
      {
        heading: '3. Действия при выявлении',
        body:
          'Сотрудник направляет уведомление в систему ПОД/ФТ-Офис в течение рабочего дня. ' +
          'Комплаенс-офицер принимает решение: признать операцию необычной, провести ' +
          'углублённую проверку или отправить отчёт в Росфинмониторинг. ' +
          'Срок направления ФЭС в Росфинмониторинг — не позднее 3 рабочих дней. ' +
          'Перечень ежеквартально пересматривается Волковым А. Н.',
      },
    ],
  },

  'cp-3': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение регламента',
        body:
          'Регламент KYC (Know Your Customer) устанавливает порядок идентификации ' +
          'и верификации новых клиентов банка: физических лиц, индивидуальных предпринимателей ' +
          'и юридических лиц. Документ определяет минимально необходимый пакет документов, ' +
          'сроки и ответственных на каждом этапе проверки.',
      },
      {
        heading: '2. Этапы и документы',
        body:
          'Физические лица: паспорт + СНИЛС + подтверждение адреса (ЖКУ до 3 мес.). ' +
          'Индивидуальные предприниматели: дополнительно — свидетельство ОГРНИП. ' +
          'Юридические лица: устав, ОГРН, ИНН, решение о назначении директора, ' +
          'сведения о бенефициарном владельце (доля > 25 %), анкета юридического лица. ' +
          'Нерезиденты: апостиль + нотариальный перевод документов.',
      },
      {
        heading: '3. Сроки верификации и обновление досье',
        body:
          'Стандартная верификация — 1 рабочий день. ' +
          'Расширенная проверка для клиентов высокого риска — до 5 рабочих дней. ' +
          'Досье обновляется: физические лица — 1 раз в 5 лет, ЮЛ — 1 раз в 3 года. ' +
          'При обнаружении расхождений — клиент уведомляется и 30 дней на исправление. ' +
          'Ответственный — Иванова А. С., регламент утверждён 26.04.2026.',
      },
    ],
  },

  'cp-4': {
    kind: 'table',
    headers: [
      'Клиент',
      'Тип операции',
      'Сумма, тыс. ₽',
      'Дата',
      'Признак подозрительности',
      'Решение комплаенс',
      'Направлено в РФМ',
    ],
    rows: [
      [
        'ООО «КэшТрейд»',
        'Снятие наличных',
        '1 850',
        '05.05.2026',
        'Группа Б (транзит > 90 %)',
        'Подтверждена сомнительность',
        'Да',
      ],
      [
        'ИП Казаков Д. С.',
        'Перевод нерезиденту',
        '2 400',
        '08.05.2026',
        'Группа Г (юрисдикция)',
        'Запрошены документы',
        'Нет',
      ],
      [
        'ООО «МикроФинТех»',
        'Дробление платежей',
        '3 × 195 тыс.',
        '11.05.2026',
        'Группа А (структурирование)',
        'Подтверждена сомнительность',
        'Да',
      ],
      [
        'Петрова Е. В.',
        'Перевод ФЛ',
        '620',
        '14.05.2026',
        'Несоответствие профилю',
        'Признана нетипичной',
        'Нет',
      ],
      [
        'АО «РесурсПром»',
        'Пополнение счёта',
        '12 000',
        '18.05.2026',
        'Операция > 600 тыс.',
        'Обязательный контроль',
        'Да',
      ],
      [
        'ООО «ТехСервис»',
        'Снятие наличных',
        '900',
        '22.05.2026',
        'Группа Б (транзит 85 %)',
        'Оперативный мониторинг',
        'Нет',
      ],
      [
        'ИП Соловьёв А. П.',
        'Покупка валюты',
        '1 100',
        '27.05.2026',
        'Несоответствие профилю',
        'Документы запрошены',
        'Нет',
      ],
    ],
  },

  'cp-5': {
    kind: 'text',
    sections: [
      {
        heading: '1. Введение',
        body:
          'Памятка подготовлена для сотрудников банка, работающих с международными ' +
          'расчётами и иностранными контрагентами. Цель — минимизировать риск ' +
          'нарушения санкционного законодательства при проведении банковских операций ' +
          'в условиях расширенных ограничений 2024–2026 годов.',
      },
      {
        heading: '2. Основные риски и запреты',
        body:
          'Запрещено открывать корреспондентские счета в банках из SDN-листа США и ' +
          'аналогичных перечней ЕС, Великобритании. ' +
          'Любые платежи в пользу лиц из SDN-листа требуют блокировки ' +
          'и немедленного уведомления комплаенс-офицера. ' +
          'Контрагенты из «высокорисковых юрисдикций» (перечень Росфинмониторинга) ' +
          'проходят расширенную KYC-проверку.',
      },
      {
        heading: '3. Порядок действий',
        body:
          'Шаг 1: проверить контрагента в системе «Санкции-Монитор» (доступна через интранет). ' +
          'Шаг 2: при совпадении — НЕ проводить операцию, уведомить комплаенс в течение 1 часа. ' +
          'Шаг 3: комплаенс уведомляет ЦБ и Росфинмониторинг. ' +
          'Памятка актуализирована 18.05.2026; ответственный — Иванова А. С.',
      },
    ],
  },

  'cp-6': {
    kind: 'text',
    sections: [
      {
        heading: '1. Правовая основа',
        body:
          'Политика обработки персональных данных разработана в соответствии с ' +
          'Федеральным законом № 152-ФЗ «О персональных данных» и приказами Роскомнадзора. ' +
          'Документ определяет цели, объём, порядок обработки и защиты персональных данных ' +
          'клиентов, сотрудников и иных субъектов, с которыми взаимодействует банк.',
      },
      {
        heading: '2. Категории данных и цели обработки',
        body:
          'Банк обрабатывает следующие категории данных: идентификационные (ФИО, паспорт, ИНН), ' +
          'контактные (телефон, email, адрес), финансовые (операции, остатки), ' +
          'биометрические (для дистанционной идентификации). ' +
          'Цели обработки: выполнение договорных обязательств, соблюдение законодательства, ' +
          'маркетинговые коммуникации (только с согласия субъекта).',
      },
      {
        heading: '3. Права субъектов и хранение данных',
        body:
          'Субъект вправе: запросить перечень обрабатываемых данных, потребовать исправления ' +
          'неточных данных, отозвать согласие на обработку. ' +
          'Срок хранения данных клиентов — 5 лет после прекращения отношений. ' +
          'Политика актуализирована 24.04.2026; размещена на сайте банка в открытом доступе. ' +
          'Ответственный — Иванова А. С.',
      },
    ],
  },

  // ── Презентации ──
  'pr-1': {
    kind: 'text',
    sections: [
      {
        heading: 'Часть 1. Текущая позиция банка',
        body:
          'По итогам 2025 года банк занимает 28-е место в рейтинге российских банков ' +
          'по размеру активов (142 млрд руб.). Рентабельность капитала (ROE) — 14,2 %, ' +
          'чистая процентная маржа — 3,8 %. Клиентская база: 48 000 юридических лиц, ' +
          '210 000 физических лиц. Основные регионы присутствия: Москва, Санкт-Петербург, ' +
          'Ростов-на-Дону.',
      },
      {
        heading: 'Часть 2. Стратегические направления 2026–2028',
        body:
          'Направление 1: Рост кредитного портфеля МСБ до 35 млрд руб. к 2028 году. ' +
          'Направление 2: Цифровизация — доля цифровых продаж 70 % к 2027 году. ' +
          'Направление 3: Операционная эффективность — Cost/Income < 45 % к 2028 году. ' +
          'Направление 4: Устойчивое развитие — внедрение ESG-критериев в кредитование.',
      },
      {
        heading: 'Часть 3. Ключевые инициативы и ресурсы',
        body:
          'Инициатива 1: Внедрение ИИ-ассистента для автоматизации рутинных операций ' +
          '(пилот — Q2 2026, полное развёртывание — Q4 2026). ' +
          'Инициатива 2: Запуск мобильного банка МСБ (Q3 2026). ' +
          'Инициатива 3: Открытие 3 новых точек продаж в регионах (2027 год). ' +
          'Бюджет IT-трансформации — 1,8 млрд руб. на 3 года. ' +
          'Ответственный — Волков А. Н., операционный директор.',
      },
    ],
  },

  'pr-2': {
    kind: 'text',
    sections: [
      {
        heading: 'Финансовые результаты Q1 2026',
        body:
          'Чистая прибыль Q1 2026 — 1 340 млн руб. (+18 % к Q1 2025). ' +
          'Чистый процентный доход — 2 870 млн руб. ' +
          'Чистый комиссионный доход — 540 млн руб. (+12 %). ' +
          'Операционные расходы — 1 620 млн руб. (Cost/Income — 47,2 %). ' +
          'Резервы на возможные потери — 410 млн руб. (снижение на 8 % к предыдущему кварталу).',
      },
      {
        heading: 'Кредитный портфель Q1 2026',
        body:
          'Совокупный кредитный портфель — 26,8 млрд руб. (+11 % с начала года). ' +
          'МСБ: 15,2 млрд руб. (57 % портфеля). ' +
          'Физические лица: 7,4 млрд руб. (28 % портфеля). ' +
          'NPL — 3,1 % (целевой уровень — 3,5 %, всё в норме). ' +
          'Крупнейшие отрасли: торговля (32 %), строительство (21 %), АПК (14 %).',
      },
      {
        heading: 'Стратегические задачи на Q2 2026',
        body:
          'Приоритет 1: Запуск пилота ИИ-ассистента (40 пользователей, 8 недель). ' +
          'Приоритет 2: Рост портфеля МСБ на 5 % за квартал. ' +
          'Приоритет 3: Улучшение NPS клиентского обслуживания с 42 до 47. ' +
          'Приоритет 4: Снижение Cost/Income до 46 % за счёт автоматизации back-office. ' +
          'Доклад подготовлен Волковым А. Н. для заседания правления 29.04.2026.',
      },
    ],
  },

  'pr-3': {
    kind: 'text',
    sections: [
      {
        heading: 'Слайд 1–3. Продуктовая линейка МСБ: обзор',
        body:
          'Банк предлагает МСБ 7 кредитных продуктов: овердрафт (до 30 % оборота), ' +
          'кредитная линия (ВКЛ и НКЛ), срочный кредит на оборотный капитал, ' +
          'инвестиционный кредит, лизинг оборудования, факторинг, гарантии. ' +
          'Средняя ставка по портфелю МСБ — 16,7 % (апрель 2026).',
      },
      {
        heading: 'Слайд 4–6. Целевая аудитория и каналы продаж',
        body:
          'Ключевые клиентские сегменты: торговля (32 %), строительство (21 %), ' +
          'производство (18 %), сельское хозяйство (14 %), услуги (15 %). ' +
          'Каналы привлечения: персональные менеджеры (50 %), мобильный/интернет-банк (35 %), ' +
          'партнёры (15 %). ' +
          'Целевой NPS клиентского сегмента МСБ — 52 по итогам 2026 года.',
      },
      {
        heading: 'Слайд 7–9. Новые продукты и цифровизация',
        body:
          'В Q3 2026 запланирован запуск «Кредит за 15 минут» для МСБ с оборотом > 10 млн руб. ' +
          'в год. Решение — автоматическое на основе скоринговой модели МСБ v2.4. ' +
          'Интеграция с ФНС для автоматического получения финансовой отчётности — Q4 2026. ' +
          'Ожидаемый прирост новых заёмщиков МСБ: +800 в год. ' +
          'Презентация подготовлена Смирновой О. И., 13.05.2026.',
      },
    ],
  },

  'pr-4': {
    kind: 'text',
    sections: [
      {
        heading: 'Слайд 1–3. Цели пилота',
        body:
          'Пилот ИИ-ассистента охватывает юридический блок и операционный департамент: ' +
          '40 пользователей, 8 недель. Цели — сократить время подготовки типовых ' +
          'документов на 30 % и время поиска по внутренней базе знаний на 50 %.',
      },
      {
        heading: 'Слайд 4–6. Архитектура и безопасность',
        body:
          'Решение разворачивается в закрытом контуре банка, модели работают на ' +
          'собственных GPU-мощностях, данные не покидают периметр. Доступ — через SSO, ' +
          'действия логируются. Источники знаний: регламенты, шаблоны договоров, ' +
          'база решений юридического отдела.',
      },
      {
        heading: 'Слайд 7–9. Результаты и следующие шаги',
        body:
          'За 4 недели пилота обработано 1 240 запросов, средняя оценка ответов 4,6/5. ' +
          'Экономия времени юристов — около 11 часов в неделю на отдел. Предлагается ' +
          'расширить пилот на кредитный департамент и подключить хранилище ДАМ ' +
          'в качестве источника.',
      },
    ],
  },

  'pr-5': {
    kind: 'text',
    sections: [
      {
        heading: 'Часть 1. Текущее состояние цифровизации',
        body:
          'К маю 2026 года банк реализовал: онлайн-открытие счетов для физических лиц (100 %), ' +
          'дистанционное кредитование физических лиц (78 % заявок), ' +
          'электронный документооборот с корпоративными клиентами (65 %). ' +
          'Индекс цифровой зрелости по методологии McKinsey Digital — 3,2 из 5.',
      },
      {
        heading: 'Часть 2. Роадмап 2026–2027',
        body:
          'Q2 2026: ИИ-ассистент (пилот), мобильный банк МСБ (бета-версия). ' +
          'Q3 2026: Открытый API для партнёров (Open Banking, ФЗ-406), ' +
          'запуск «Кредит за 15 минут» для МСБ. ' +
          'Q4 2026: Полное развёртывание ИИ на весь банк, интеграция с платформой ЦБ. ' +
          'Q1 2027: Голосовой банкинг, предиктивная аналитика для менеджеров.',
      },
      {
        heading: 'Часть 3. Бюджет и риски',
        body:
          'Общий IT-бюджет на трансформацию — 1,8 млрд руб. (2026–2027). ' +
          'Ключевые риски: интеграция с legacy-системами (высокий), ' +
          'дефицит IT-специалистов (средний), регуляторные изменения ЦБ (средний). ' +
          'Митигация: привлечение интегратора «Синимекс», найм 4 senior-разработчиков Q3 2026. ' +
          'Ответственный — Волков А. Н.',
      },
    ],
  },

  // ── Отчётность (xlsx) ──
  'rp-1': {
    kind: 'table',
    headers: [
      'Код строки',
      'Наименование показателя',
      'На 01.04.2026, тыс. ₽',
      'На 01.05.2026, тыс. ₽',
    ],
    rows: [
      ['101', 'Денежные средства', '1 245 300', '1 310 450'],
      ['202', 'Средства в Банке России', '3 480 120', '3 391 700'],
      ['305', 'Кредиты юридическим лицам', '18 765 400', '19 042 850'],
      ['306', 'Кредиты физическим лицам', '7 214 900', '7 388 200'],
      ['401', 'Средства клиентов до востребования', '9 871 350', '10 102 600'],
      ['402', 'Срочные депозиты', '14 332 700', '14 518 900'],
      ['501', 'Собственные средства (капитал)', '5 940 200', '5 987 400'],
    ],
  },

  'rp-2': {
    kind: 'table',
    headers: [
      'Норматив',
      'Наименование',
      'Предельное значение',
      'На 01.04.2026, %',
      'На 01.05.2026, %',
      'Выполнение',
    ],
    rows: [
      ['Н1.0', 'Достаточность капитала', '≥ 8,0 %', '12,4', '12,7', 'Выполнен'],
      ['Н1.1', 'Достаточность базового капитала', '≥ 4,5 %', '9,1', '9,3', 'Выполнен'],
      ['Н1.2', 'Достаточность основного капитала', '≥ 6,0 %', '10,2', '10,5', 'Выполнен'],
      ['Н2', 'Мгновенная ликвидность', '≥ 15 %', '68,4', '71,2', 'Выполнен'],
      ['Н3', 'Текущая ликвидность', '≥ 50 %', '84,7', '87,1', 'Выполнен'],
      ['Н4', 'Долгосрочная ликвидность', '≤ 120 %', '78,3', '76,9', 'Выполнен'],
      ['Н6', 'Макс. риск на заёмщика', '≤ 25 %', '18,2', '19,1', 'Выполнен'],
    ],
  },

  'rp-3': {
    kind: 'table',
    headers: [
      'Показатель',
      'Подразделение',
      'Апрель 2026, млн ₽',
      'Май 2026, млн ₽',
      'Изменение, %',
      'План май, млн ₽',
      'Выполнение плана',
    ],
    rows: [
      ['Процентные доходы', 'Кредитный блок', '910', '940', '+3,3 %', '920', '102 %'],
      ['Комиссионные доходы', 'Операционный блок', '175', '182', '+4,0 %', '180', '101 %'],
      ['Торговые доходы', 'Казначейство', '42', '38', '-9,5 %', '45', '84 %'],
      ['Процентные расходы', 'Казначейство', '-520', '-535', '+2,9 %', '-530', 'В норме'],
      ['Операционные расходы', 'Все подразд.', '-530', '-545', '+2,8 %', '-540', 'В норме'],
      ['Резервы', 'Риски', '-140', '-128', '-8,6 %', '-145', 'Ниже плана (лучше)'],
      ['Чистая прибыль', 'Итого', '435', '452', '+3,9 %', '440', '103 %'],
    ],
  },

  'rp-4': {
    kind: 'table',
    headers: [
      'KPI',
      'Подразделение',
      'Ед. изм.',
      'Цель апрель',
      'Факт апрель',
      'Выполнение',
      'Тренд',
    ],
    rows: [
      [
        'Кол-во обработанных платёжных поручений',
        'Операционный блок',
        'шт.',
        '45 000',
        '47 320',
        '105 %',
        '↑',
      ],
      [
        'Среднее время обработки платежа',
        'Операционный блок',
        'мин.',
        '≤ 12',
        '10,4',
        'Выполнен',
        '↑',
      ],
      ['Доля ошибочных операций', 'Операционный блок', '%', '≤ 0,3 %', '0,18 %', 'Выполнен', '↑'],
      [
        'SLA по обращениям клиентов (ответ ≤ 4 ч)',
        'Клиентский сервис',
        '%',
        '≥ 95 %',
        '96,4 %',
        'Выполнен',
        '→',
      ],
      ['NPS клиентского обслуживания', 'Клиентский сервис', 'баллы', '≥ 42', '44', 'Выполнен', '↑'],
      [
        'Операционные расходы / доходы (Cost/Income)',
        'Финансы',
        '%',
        '≤ 48 %',
        '47,5 %',
        'Выполнен',
        '↑',
      ],
    ],
  },

  'rp-5': {
    kind: 'text',
    sections: [
      {
        heading: '1. Состав и сроки SLA',
        body:
          'Отчёт охватывает показатели соответствия соглашениям об уровне сервиса (SLA) ' +
          'IT-поддержки и инфраструктуры за I квартал 2026 года. ' +
          'Отслеживаемые метрики: доступность ключевых систем, среднее время реакции ' +
          'на инцидент (MTTR), удовлетворённость пользователей поддержкой.',
      },
      {
        heading: '2. Ключевые показатели Q1 2026',
        body:
          'Доступность АБС (АС «Диасофт FLEXTERA»): 99,6 % (SLA ≥ 99,5 %) — выполнено. ' +
          'Доступность интернет-банка: 99,9 % — выполнено. ' +
          'MTTR критических инцидентов (Priority 1): 47 мин. (SLA ≤ 60 мин.) — выполнено. ' +
          'Удовлетворённость поддержкой (CSAT): 4,3 из 5. ' +
          'Всего инцидентов P1 за Q1: 3 (все устранены без нарушения SLA).',
      },
      {
        heading: '3. Планы на Q2 2026',
        body:
          'Обновление инфраструктуры серверного зала (перевод на новое оборудование): ' +
          'плановое окно — выходные 21–22 июня 2026. ' +
          'Переход на новую систему мониторинга (Zabbix → Prometheus + Grafana) — июнь 2026. ' +
          'Ответственный за SLA — IT-директор банка.',
      },
    ],
  },

  'rp-6': {
    kind: 'text',
    sections: [
      {
        heading: '1. Основания и предмет аудита',
        body:
          'Независимый аудит финансовой отчётности банка за 2025 год проведён ' +
          'АО «Аудит-Консалт» в соответствии с международными стандартами аудита (МСА). ' +
          'Предмет: годовой бухгалтерский баланс, отчёт о финансовых результатах, ' +
          'отчёт о движении денежных средств и примечания к ним.',
      },
      {
        heading: '2. Аудиторское мнение',
        body:
          'По нашему мнению, прилагаемая финансовая отчётность отражает достоверно ' +
          'во всех существенных отношениях финансовое положение банка по состоянию ' +
          'на 31 декабря 2025 года, результаты его деятельности и движение денежных средств ' +
          'в соответствии с МСФО. Мнение выражено без оговорок.',
      },
      {
        heading: '3. Ключевые вопросы аудита',
        body:
          'Резервы под обесценение кредитного портфеля: проверена корректность применения ' +
          'модели ожидаемых кредитных убытков (ECL по МСФО 9). Замечаний нет. ' +
          'Оценка залогового имущества: проверены методология и документация оценщиков. ' +
          'Рекомендовано усилить периодичность мониторинга залогов МСБ. ' +
          'Аудитор — АО «Аудит-Консалт», заключение подписано 08.04.2026.',
      },
    ],
  },

  // ── Шаблоны документов ──
  'tp-1': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение бланка',
        body:
          'Бланк предназначен для оформления исходящих писем банка сторонним организациям, ' +
          'государственным органам, контрагентам и клиентам. ' +
          'Использование официального бланка обязательно для всей внешней переписки. ' +
          'Шаблон содержит логотип, реквизиты и установленные поля для реквизитов письма.',
      },
      {
        heading: '2. Структура письма',
        body:
          'Шапка: наименование банка, адрес, тел./факс, ОГРН, ИНН/КПП, БИК. ' +
          'Реквизиты: исходящий номер, дата, кому. ' +
          'Тело: заголовок, приветствие, основной текст, заключение, просьба/вопрос. ' +
          'Подпись: должность, ФИО, подпись, дата. ' +
          'Для писем с грифом «Коммерческая тайна» — специальная пометка на бланке.',
      },
      {
        heading: '3. Порядок применения',
        body:
          'Исходящие письма регистрируются в СЭД с присвоением исходящего номера. ' +
          'Подписант — уполномоченное должностное лицо согласно матрице полномочий. ' +
          'Копия письма сохраняется в СЭД 5 лет. ' +
          'Шаблон создан Смирновой О. И. и согласован 09.04.2026.',
      },
    ],
  },

  'tp-2': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение шаблона',
        body:
          'Шаблон протокола предназначен для документирования решений, принятых ' +
          'на заседаниях комитетов, совещаниях рабочих групп и правлений банка. ' +
          'Протокол является официальным документом и хранится в установленном порядке в СЭД.',
      },
      {
        heading: '2. Структура протокола',
        body:
          'Заголовок: вид совещания, номер, дата, место, время начала и окончания. ' +
          'Участники: председатель, присутствующие, кворум. ' +
          'Повестка дня: пронумерованный список вопросов. ' +
          'По каждому вопросу: СЛУШАЛИ, ВЫСТУПИЛИ, РЕШИЛИ. ' +
          'Поручения: ответственный, срок. ' +
          'Подписи: председатель, секретарь.',
      },
      {
        heading: '3. Инструкция',
        body:
          'Протокол оформляется секретарём совещания в течение 1 рабочего дня. ' +
          'После согласования с председателем — регистрируется в СЭД и рассылается участникам. ' +
          'Хранение: постоянно для заседаний правления и кредитного комитета; ' +
          '5 лет для рабочих совещаний. Шаблон согласован 09.04.2026.',
      },
    ],
  },

  'tp-3': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение шаблона',
        body:
          'Шаблон служебной записки предназначен для внутренней переписки между ' +
          'структурными подразделениями банка: запросы информации, согласования, ' +
          'уведомления, предложения. Служебная записка не является официальным ' +
          'внешним документом и не подлежит регистрации в качестве исходящего письма.',
      },
      {
        heading: '2. Структура записки',
        body:
          'Кому: должность, ФИО получателя. ' +
          'От кого: должность, ФИО отправителя. ' +
          'Дата и номер (при необходимости регистрации). ' +
          'Тема: краткое содержание (1 строка). ' +
          'Основной текст: изложение сути, при необходимости — таблицы и приложения. ' +
          'Подпись и дата.',
      },
      {
        heading: '3. Применение',
        body:
          'Служебные записки, содержащие финансовые запросы или поручения, ' +
          'подписываются руководителем подразделения. ' +
          'Для срочных вопросов — направляется по корпоративной почте с пометкой «Срочно». ' +
          'Шаблон создан Смирновой О. И., согласован 09.04.2026.',
      },
    ],
  },

  'tp-4': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение заявления',
        body:
          'Форма заявления применяется для оформления запроса сотрудника на предоставление ' +
          'ежегодного оплачиваемого отпуска, отпуска без сохранения заработной платы, ' +
          'учебного отпуска или переноса запланированного отпуска. ' +
          'Заявление подаётся не позднее чем за 14 дней до начала отпуска.',
      },
      {
        heading: '2. Порядок подачи',
        body:
          'Заявление заполняется в электронном виде в HR-системе или в бумажном виде ' +
          'на данном бланке. Визируется непосредственным руководителем. ' +
          'Передаётся HR-партнёру. HR формирует приказ об отпуске в течение 3 рабочих дней. ' +
          'Приказ подписывается уполномоченным должностным лицом и направляется сотруднику.',
      },
      {
        heading: '3. Реквизиты заявления',
        body:
          'Кому: руководитель банка (ФИО). ' +
          'От кого: должность, подразделение, ФИО, табельный номер. ' +
          'Вид отпуска, дата начала, количество дней, дата окончания. ' +
          'Подпись и дата. Виза руководителя: «Согласовано» / «Отказано», подпись, дата. ' +
          'Шаблон согласован HR (Смирнова О. И.) 21.04.2026.',
      },
    ],
  },

  'tp-5': {
    kind: 'text',
    sections: [
      {
        heading: '1. Назначение шаблона приказа',
        body:
          'Шаблон предназначен для оформления приказов по основной деятельности банка: ' +
          'утверждение локальных нормативных актов, изменение организационной структуры, ' +
          'ввод в действие регламентов и политик, назначение ответственных лиц. ' +
          'Приказы о приёме/увольнении сотрудников оформляются по унифицированным формам Т-1/Т-8.',
      },
      {
        heading: '2. Структура приказа',
        body:
          'Шапка: наименование банка, вид документа «ПРИКАЗ», номер, дата, место. ' +
          'Заголовок: «О …» (краткое содержание). ' +
          'Констатирующая часть: основание (НПА, производственная необходимость). ' +
          'Распорядительная часть: «ПРИКАЗЫВАЮ:» — пронумерованные пункты. ' +
          'Контроль исполнения: ответственный. ' +
          'Подпись уполномоченного лица.',
      },
      {
        heading: '3. Порядок регистрации и хранения',
        body:
          'Приказы регистрируются в журнале приказов по основной деятельности в СЭД. ' +
          'Нумерация — сквозная в пределах года. ' +
          'Оригиналы хранятся постоянно. ' +
          'Ознакомление сотрудников — электронная подпись в СЭД или подпись на бумажной копии. ' +
          'Шаблон разработан юридическим блоком (Петров М. В.), согласован 02.05.2026.',
      },
    ],
  },
};
